import { api } from '@/lib/api'
import type { SelectOption } from '@/components/SearchableSelect'
import {
  GENDER,
  JOB_PREFERENCES,
  MARITAL_STATUS,
  type EducationRow,
  type OrganizationRow,
  type PersonalInfoState,
  type WorkExperienceRow,
} from './types'

/**
 * CV autofill.
 *
 * Port of `ProfileForm.handleCVFile` -> `setCVData` -> `triggerAutofill` and
 * `PersonalInfo.autofillFromCV`. The uploaded PDF goes to
 * POST /api/ecareer/gemini/convertCV, which extracts the text and asks Gemini to
 * return it as the JSON template pinned in gemini/vertex.ts, and the result is
 * mapped onto the form.
 *
 * The model's output is untrusted and loosely typed: keys arrive in whatever
 * case Gemini produced, numbers arrive as strings, and any field may be missing
 * or contain the literal placeholder from the template (`"<string>"`). Every
 * read below goes through a helper that tolerates all of that; a value that
 * cannot be understood is skipped rather than written as-is.
 */
export interface CvExtract {
  personalInfo?: Record<string, unknown>
  education?: Record<string, unknown>[]
  workExperience?: Record<string, unknown>[]
  organizations?: Record<string, unknown>[]
}

interface ConvertCvResponse {
  success: boolean
  data?: string | CvExtract
  message?: string
}

/** POST the PDF and return the parsed extraction, or null when nothing usable came back. */
export async function convertCv(file: File): Promise<CvExtract | null> {
  const form = new FormData()
  form.append('CvFile', file)

  const response = await api.form<ConvertCvResponse>('/api/ecareer/gemini/convertCV', form)
  if (!response.success || !response.data) return null

  // The endpoint returns the model's reply as a JSON *string*; older shapes
  // returned it already parsed. Both are accepted, as the original did.
  if (typeof response.data !== 'string') return response.data
  try {
    return JSON.parse(response.data) as CvExtract
  } catch {
    return null
  }
}

/* --------------------------- value helpers --------------------------- */

/** Template placeholders like "<string>" and "<integer>" are not real values. */
function isPlaceholder(value: string): boolean {
  return /^<.*>$/.test(value.trim())
}

/** Reads the first present key, case-insensitively, ignoring blanks and placeholders. */
function pick(source: Record<string, unknown> | undefined, ...keys: string[]): string {
  if (!source) return ''
  const lower = new Map(Object.entries(source).map(([k, v]) => [k.toLowerCase(), v]))
  for (const key of keys) {
    const raw = lower.get(key.toLowerCase())
    if (raw === null || raw === undefined) continue
    const text = Array.isArray(raw) ? String(raw[0] ?? '') : String(raw)
    if (!text.trim() || isPlaceholder(text)) continue
    return text.trim()
  }
  return ''
}

function pickNumber(source: Record<string, unknown> | undefined, ...keys: string[]): string {
  const text = pick(source, ...keys)
  if (!text) return ''
  const digits = /-?\d+(\.\d+)?/.exec(text)
  return digits ? digits[0] : ''
}

function pickBoolean(source: Record<string, unknown> | undefined, ...keys: string[]): boolean {
  return pick(source, ...keys).toLowerCase() === 'true'
}

/**
 * Money, as digits only.
 *
 * `pickNumber` cannot be used here: Indonesian amounts are written
 * "Rp 12.000.000", where the dots are thousands separators, and its
 * decimal-aware regex stops at "12.000". Salary has no fractional part in
 * practice, so every non-digit is simply dropped.
 */
function pickAmount(source: Record<string, unknown> | undefined, ...keys: string[]): string {
  const digits = pick(source, ...keys).replace(/\D/g, '')
  return digits
}

/** `PersonalInfo.setGender` — accepts 1/2, Male/Female and Pria/Wanita. */
function mapGender(value: string): number | null {
  if (!value) return null
  if (value === '1') return GENDER.Male
  if (value === '2') return GENDER.Female
  const lower = value.toLowerCase()
  if (lower === 'male' || lower === 'pria' || lower === 'laki-laki') return GENDER.Male
  if (lower === 'female' || lower === 'wanita' || lower === 'perempuan') return GENDER.Female
  return null
}

/** `ProfileForm.mapMaritalStatusFromApi`, including its Indonesian synonyms. */
function mapMaritalStatus(value: string): number | null {
  if (!value) return null
  if (value === '1') return MARITAL_STATUS.Single
  if (value === '2') return MARITAL_STATUS.Married
  if (value === '3') return MARITAL_STATUS.Divorced
  if (value === '4') return MARITAL_STATUS.Widow
  const lower = value.toLowerCase()
  if (lower.includes('single') || lower.includes('belum')) return MARITAL_STATUS.Single
  if (lower.includes('married') || lower.includes('menikah')) return MARITAL_STATUS.Married
  if (lower.includes('divorce') || lower.includes('cerai')) return MARITAL_STATUS.Divorced
  if (lower.includes('widow') || lower.includes('janda') || lower.includes('duda')) {
    return MARITAL_STATUS.Widow
  }
  return null
}

/** Normalises whatever date shape the model returned to "yyyy-MM-dd". */
function mapDate(value: string): string {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
}

function ageFrom(dateOfBirth: string): string {
  const born = new Date(dateOfBirth)
  if (Number.isNaN(born.getTime())) return ''
  const today = new Date()
  let age = today.getFullYear() - born.getFullYear()
  const monthDelta = today.getMonth() - born.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < born.getDate())) age--
  return String(Math.max(age, 0))
}

/** Finds a lookup option whose label matches the extracted text. */
function matchOption(options: SelectOption[], text: string): SelectOption | undefined {
  if (!text) return undefined
  const needle = text.trim().toLowerCase()
  return (
    options.find((option) => option.label.toLowerCase() === needle) ??
    options.find((option) => option.label.toLowerCase().includes(needle)) ??
    options.find((option) => needle.includes(option.label.toLowerCase()))
  )
}

/* ------------------------------ mappers ------------------------------ */

/**
 * Personal fields.
 *
 * Email is deliberately NOT filled: it identifies the account, the field is
 * read-only, and the server overrides it from the session anyway. The original
 * had the same line commented out for the same reason.
 */
export function personalInfoFromCv(
  extract: CvExtract,
  provinces: SelectOption[],
): Partial<PersonalInfoState> {
  const info = extract.personalInfo
  if (!info) return {}

  const patch: Partial<PersonalInfoState> = {}

  const fullName = pick(info, 'fullName', 'FullName')
  if (fullName) patch.FullName = fullName

  const phone = pick(info, 'phoneNumber', 'PhoneNumber')
  if (phone) patch.PhoneNumber = phone

  const placeOfBirth = pick(info, 'placeOfBirth', 'PlaceOfBirth')
  if (placeOfBirth) patch.PlaceOfBirth = placeOfBirth

  const dateOfBirth = mapDate(pick(info, 'dateOfBirth', 'DateOfBirth'))
  if (dateOfBirth) {
    patch.DateOfBirth = dateOfBirth
    patch.Age = ageFrom(dateOfBirth)
  } else {
    const age = pickNumber(info, 'age', 'Age')
    if (age) patch.Age = age
  }

  const linkedIn = pick(info, 'linkedIn', 'LinkedIn', 'LinkedinAccount')
  if (linkedIn) patch.LinkedIn = linkedIn

  const instagram = pick(info, 'instagram', 'Instagram', 'InstagramAccount')
  if (instagram) patch.Instagram = instagram

  const address = pick(info, 'address', 'Address')
  if (address) patch.Address = address

  // Domicile is a province id upstream, so the extracted text has to resolve to
  // a real option — a free-text city name is dropped rather than sent as a
  // non-numeric State_HCIS_ID, which saveAll rejects outright.
  const domicile = matchOption(provinces, pick(info, 'domicile', 'Domicile'))
  if (domicile) {
    patch.DomicileId = domicile.value
    patch.DomicileName = domicile.label
  }

  const jobPreference = matchOption(
    JOB_PREFERENCES.map((p) => ({ value: p.value, label: p.label })),
    pick(info, 'jobPreferences', 'JobPreferences'),
  )
  if (jobPreference) {
    patch.JobPreferences = jobPreference.value
    patch.JobPreferencesLabel = jobPreference.label
  }

  const gender = mapGender(pick(info, 'gender', 'Gender'))
  if (gender !== null) patch.Gender = gender

  const marital = mapMaritalStatus(pick(info, 'maritalStatus', 'MaritalStatus', 'mstMaritalStatusID'))
  if (marital !== null) patch.MaritalStatusId = marital

  return patch
}

export function educationFromCv(
  extract: CvExtract,
  degrees: SelectOption[],
  universities: SelectOption[],
  majors: SelectOption[],
): EducationRow[] {
  return (extract.education ?? [])
    .map((item): EducationRow => {
      const degreeText = pick(item, 'DegreeName', 'degreeName')
      const universityText = pick(item, 'UniversityName', 'universityName', 'UniversityOther')
      const majorText = pick(item, 'MajorName', 'majorName', 'MajorOther')

      const degree = matchOption(degrees, degreeText)
      const university = matchOption(universities, universityText)
      const major = matchOption(majors, majorText)

      return {
        mstDegreeID: degree?.value ?? '',
        DegreeName: degree?.label ?? degreeText,
        // No master-data match means "Other": id 0 with the free text alongside,
        // which is the shape saveAll expects.
        mstUniversityID: university?.value ?? (universityText ? '0' : ''),
        UniversityName: university?.label ?? universityText,
        UniversityOther: university ? '' : universityText,
        mstMajorID: major?.value ?? (majorText ? '0' : ''),
        MajorName: major?.label ?? majorText,
        MajorOther: major ? '' : majorText,
        GPA: pickNumber(item, 'GPA', 'gpa'),
        MonthIn: pickNumber(item, 'MonthIn', 'monthIn'),
        YearIn: pickNumber(item, 'YearIn', 'yearIn'),
        MonthOut: pickNumber(item, 'MonthOut', 'monthOut'),
        YearOut: pickNumber(item, 'YearOut', 'yearOut'),
        isUntilNow: pickBoolean(item, 'isUntilNow'),
      }
    })
    .filter((row) => row.UniversityName || row.UniversityOther || row.DegreeName)
}

export function workExperienceFromCv(
  extract: CvExtract,
  industries: SelectOption[],
  levels: SelectOption[],
): WorkExperienceRow[] {
  return (extract.workExperience ?? [])
    .map((item): WorkExperienceRow => {
      const industry = levels.length ? matchOption(industries, pick(item, 'IndustryName')) : undefined
      const levelId = pickNumber(item, 'mstWorkExperienceLevelID')
      const level = levels.find((option) => option.value === levelId)

      return {
        Company: pick(item, 'Company', 'company'),
        mstIndustryID: industry?.value ?? '',
        IndustryName: industry?.label ?? '',
        Position: pick(item, 'Position', 'position'),
        mstWorkExperienceLevelID: level?.value ?? '',
        LevelName: level?.label ?? '',
        MonthIn: pickNumber(item, 'MonthIn', 'monthIn'),
        YearIn: pickNumber(item, 'YearIn', 'yearIn'),
        MonthOut: pickNumber(item, 'MonthOut', 'monthOut'),
        YearOut: pickNumber(item, 'YearOut', 'yearOut'),
        isUntilNow: pickBoolean(item, 'isUntilNow'),
        Salary: pickAmount(item, 'Salary', 'salary'),
        JobDescription: pick(item, 'JobDescription', 'jobDescription'),
      }
    })
    .filter((row) => row.Company || row.Position)
}

export function organizationsFromCv(
  extract: CvExtract,
  types: SelectOption[],
): OrganizationRow[] {
  return (extract.organizations ?? [])
    .map((item): OrganizationRow => {
      const type = matchOption(types, pick(item, 'OrganizationType', 'organizationType'))
      // The prompt constrains this to Leader or Member; 1 = Leader, 2 = Member.
      const positionText = pick(
        item,
        'OrganizationPosition',
        'organizationPosition',
        'OrganizationPositionName',
      ).toLowerCase()
      const position = positionText.includes('leader')
        ? '1'
        : positionText.includes('member')
          ? '2'
          : ''

      return {
        OrganizationName: pick(item, 'OrganizationName', 'organizationName'),
        mstOrganizationTypeID: type?.value ?? '',
        OrganizationTypeName: type?.label ?? '',
        OrganizationPosition: position,
      }
    })
    .filter((row) => row.OrganizationName)
}
